import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication, InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { getUserRolesOnClaim } from '../api/Users';

export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_CLIENT_ID,
    redirectUri: process.env.REACT_APP_REDIRECT_URI,
    authority: process.env.REACT_APP_AUTHORITY,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: true,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [loading, setLoading] = useState(true); // Set to true to show loading state initially
  const [isAuthenticated, setIsAuthenticated] = useState(accounts.length > 0);
  const [isRolesSet, setIsRolesSet] = useState(false);
  const [user, setUser] = useState(null); // State to hold user data
  const [roles, setRoles] = useState([]); // State to hold user roles, default to "User" for testing
  const navigate = useNavigate();

  const getUserRole = () => {
    return roles.includes("poweruser") ? "poweruser" : "basic";
  };

  const isRole = (role) => {
    return roles.includes(role.toLowerCase());
  };

  const initializeAndFetchRoles = async () => {

    try {
      await instance.initialize(); // Säkerställer att MSAL är redo innan vi använder det

      if (accounts.length > 0) {
        const token = await getAccessToken(); // Hämtar access-token
        const userRoles = await getUserRolesOnClaim(token);
        setRoles(userRoles.map(role => role.roleName.toLowerCase())); // Uppdaterar roller
        setIsRolesSet(true);
      } else {
        setRoles([]); // Om användaren loggar ut, töm roller
       
      }
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      if(isAuthenticated) {
        setIsRolesSet(true);
      }
    }
  };
  
  useEffect(() => {
  
    initializeAndFetchRoles();
  }, [accounts, instance, isAuthenticated]);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return; // Vänta på att interaktion är klar
  
    instance.handleRedirectPromise()
      .then((response) => {
        if (response) {
          instance.setActiveAccount(response.account);
          setIsAuthenticated(true);
          updateUserData(); // Update user data when authenticated
        
        } else if (accounts.length > 0) {
          instance.setActiveAccount(accounts[0]);
          setIsAuthenticated(true);
          updateUserData(); // Update user data when authenticated
       
        } else {
          setIsAuthenticated(false);
        }
      })
      .catch((error) => {
        console.error("Error handling redirect:", error);
      })
      .finally(() => setLoading(false));
  
  }, [instance, accounts, inProgress]);
  
  useEffect(() => {
    const checkAuth = () => {
      if (accounts.length > 0) {
        setIsAuthenticated(true);
        updateUserData(); // Update user data when authenticated
      } else {
        setIsAuthenticated(false);
        setUser(null); // Clear user data when logged out
        setRoles([]); // Clear roles when logged out
      }
    };
    
    const callbackId = instance.addEventCallback((message) => {
      if (message.eventType === 'LOGIN_SUCCESS' || message.eventType === 'LOGOUT_SUCCESS') {
        checkAuth();
        if (message.eventType === 'LOGIN_SUCCESS') {
          const redirectPath = sessionStorage.getItem('redirectPath') || "/";
          sessionStorage.removeItem('redirectPath');
          navigate(redirectPath);
        }
      }
    });
    
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [accounts, instance, navigate]);

  
  const updateUserData = useCallback(() => {
    if (accounts.length > 0) {
      const account = accounts[0]; // Assuming only one account, take the first one
      const userData = {
        name: account.name,
        username: account.username,
        homeAccountId: account.homeAccountId,
        localAccountId: account.localAccountId,
        environment: account.environment,
      };
      setUser(userData);
    } else {
      setUser(null);
      setRoles([]); // Clear roles
    }
  }, [accounts]);
  
  const login = () => {
    if (inProgress !== InteractionStatus.None) {
      console.warn("Login is already in progress. Ignoring duplicate request.");
      return;
    }
  
    instance.loginRedirect().catch((error) => {
      console.error("Login failed: ", error);
    });
  };

  const logout = () => {
    if (inProgress === InteractionStatus.None) {  // Check if no interaction is in progress
      instance.logoutRedirect();
    }
  };

  const getAccessToken = async () => {
    
    if (!isAuthenticated) {
      throw new Error("User is not authenticated");
    }

    if (!instance) {
      throw new Error("MSAL instance is not initialized");
    }

    const request = {

      scopes: process.env.REACT_APP_API_SCOPE.split(","),
      account: instance.getActiveAccount() || accounts[0],
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {

      if (error instanceof InteractionRequiredAuthError) {
        if (inProgress === InteractionStatus.None) {  // Ensure no other interaction is in progress
          return instance.acquireTokenRedirect(request);
        }
      } else {
        console.error("Token acquisition failed:", error);
        throw error;
      }
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, getAccessToken, loading, getUserRole, setRoles, isRole, isRolesSet, initializeAndFetchRoles }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
